/** <scapescene-group-screen>: onboarding page for the active group.
 *
 * store.group may still be null on first render (skeleton, filled in on
 * store change). Polling is started here and intentionally never stopped
 * on disconnect — the member list and group fields should stay live
 * whenever this screen (or a sibling screen sharing the same group) is
 * mounted during the session. */

import {
  renderGroupCard,
  renderMemberList,
  type Rendered,
} from "@starling-cloud/sturnia-workshop";

import { store } from "../store";
import { router } from "../control/router";
import { ApiError } from "../data/client";
import { frameCount, pickList } from "../data/definitions";
import type { GroupPatch } from "../data/types";
import { pick, t, tn } from "../lib/i18n";

import "../components/scapescene-filmstrip";

const SAVE_DEBOUNCE_MS = 600;
const SAVED_FLASH_MS = 1500;

/** Tiny local debounce helper — this screen owns its own autosave, it does
 * not import src/data/autosave.ts. */
function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
): (...args: A) => void {
  let timer: number | null = null;
  return (...args: A) => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      fn(...args);
    }, ms);
  };
}

const GROUP_PICK_KEYS = ["light", "timeOfDay", "weather"] as const;
type GroupPickKey = (typeof GROUP_PICK_KEYS)[number];

class WorkshopGroupScreen extends HTMLElement {
  #unsubscribe: (() => void) | null = null;
  #built = false;
  #savedTimer: number | null = null;
  #shared: Rendered[] = [];
  #debouncedPatch = debounce((patch: GroupPatch) => void this.#patch(patch), SAVE_DEBOUNCE_MS);

  connectedCallback(): void {
    this.#build();
    this.#update();
    store.startPolling();
    this.#unsubscribe = store.onChange(() => this.#update());
  }

  disconnectedCallback(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    if (this.#savedTimer !== null) window.clearTimeout(this.#savedTimer);
    for (const rendered of this.#shared) rendered.destroy();
    this.#shared = [];
    this.#built = false;
  }

  #build(): void {
    if (this.#built) return;
    this.#built = true;

    this.innerHTML = `
      <scapescene-filmstrip position="setup"></scapescene-filmstrip>
      <div class="screen group-screen">
        <div class="error-banner js-error" hidden></div>

        <div class="group-screen__how">
          <h2>${escapeHtml(t("howTitle"))}</h2>
          <p class="js-how-intro"></p>
          <ol class="group-screen__beats js-beats"></ol>
        </div>

        <div class="field">
          <label for="group-title">${escapeHtml(t("projectTitle"))}</label>
          <input type="text" id="group-title" class="js-title" placeholder="${escapeHtml(t("untitledFilm"))}" />
          <span class="hint js-saved-hint group-screen__saved" hidden>${escapeHtml(t("saved"))}</span>
        </div>

        <div class="js-group-card"></div>

        <div class="field">
          <label for="group-change">${escapeHtml(t("changeLabel"))}</label>
          <textarea id="group-change" class="js-change" rows="3" placeholder="${escapeHtml(t("changePlaceholder"))}"></textarea>
          <p class="hint">${escapeHtml(t("changeHint"))}</p>
        </div>

        <div class="group-screen__picks js-picks"></div>

        <div class="js-member-list"></div>

        <button type="button" class="ss-btn ss-btn--primary group-screen__start js-start">
          ${escapeHtml(t("startStoryboarding"))}
        </button>
      </div>
    `;

    const titleInput = this.querySelector<HTMLInputElement>(".js-title")!;
    titleInput.addEventListener("input", () => {
      this.#debouncedPatch({ projectTitle: titleInput.value });
    });

    const changeInput = this.querySelector<HTMLTextAreaElement>(".js-change")!;
    changeInput.addEventListener("input", () => {
      this.#debouncedPatch({ changeSentence: changeInput.value });
    });

    const startBtn = this.querySelector<HTMLButtonElement>(".js-start")!;
    startBtn.addEventListener("click", () => router.navigate("/frame/1"));

    const strip = this.querySelector("scapescene-filmstrip")!;
    strip.addEventListener("frame-change", (event) => {
      const detail = (event as CustomEvent<{ position: number }>).detail;
      router.navigate(`/frame/${detail.position}`);
    });
    strip.addEventListener("film-open", () => router.navigate("/film"));

    // The shared group card (name, code, QR) and member list from
    // sturnia-workshop — identical in every workshop app.
    this.#shared.push(
      renderGroupCard(this.querySelector<HTMLElement>(".js-group-card"), {
        session: store,
        labels: {
          name: t("ssGroupName"),
          namePlaceholder: t("ssGroupNamePlaceholder"),
          code: t("ssCodeLabel"),
          scanHint: t("ssScanHint"),
        },
      }),
      renderMemberList(this.querySelector<HTMLElement>(".js-member-list"), {
        session: store,
        labels: {
          title: t("ssMembers"),
          addPlaceholder: t("ssAddPlaceholder"),
          addButton: t("ssAddButton"),
          addHint: t("ssAddHint"),
          you: t("ssYou"),
          removeConfirm: t("ssRemoveConfirm"),
        },
      }),
    );

    this.#buildHow();
    this.#buildPicks();
  }

  /** The story-shape explainer: what the frames are, and — the thing
   * every first-time group gets wrong — that the big moment goes at the
   * end, not in picture 1. Built from the definitions so the count and
   * the beat names always match the worksheet. */
  #buildHow(): void {
    const definitions = store.definitions;
    if (!definitions) return;

    const intro = this.querySelector<HTMLElement>(".js-how-intro");
    if (intro) intro.textContent = tn("howIntro", frameCount(definitions));

    const beats = this.querySelector<HTMLElement>(".js-beats");
    if (!beats) return;
    beats.innerHTML = "";
    for (const frame of definitions.frames) {
      const item = document.createElement("li");
      const name = document.createElement("strong");
      name.textContent = pick(frame.name);
      item.appendChild(name);
      item.appendChild(
        document.createTextNode(` — ${pick(frame.subtitle)}`),
      );
      beats.appendChild(item);
    }
  }

  #buildPicks(): void {
    const container = this.querySelector<HTMLElement>(".js-picks");
    if (!container) return;
    const definitions = store.definitions;
    if (!definitions) return;

    container.innerHTML = "";
    for (const key of GROUP_PICK_KEYS) {
      const list = pickList(definitions, key);
      if (!list || list.scope !== "group") continue;

      const field = document.createElement("div");
      field.className = "field";
      field.dataset.pickKey = key;

      const label = document.createElement("label");
      label.setAttribute("for", `group-pick-${key}`);
      label.textContent = pick(list.label);
      field.appendChild(label);

      const select = document.createElement("select");
      select.id = `group-pick-${key}`;
      select.className = `js-pick-${key}`;

      const blank = document.createElement("option");
      blank.value = "";
      blank.textContent = "—";
      select.appendChild(blank);

      for (const option of list.options) {
        const opt = document.createElement("option");
        opt.value = option.value;
        opt.textContent = pick(option.label);
        select.appendChild(opt);
      }

      select.addEventListener("change", () => {
        void this.#patch({ [key]: select.value } as GroupPatch);
      });

      field.appendChild(select);
      container.appendChild(field);
    }
  }

  #activeElement(): Element | null {
    const root = this.getRootNode();
    if (root instanceof ShadowRoot || root instanceof Document) {
      return root.activeElement;
    }
    return null;
  }

  async #patch(patch: GroupPatch): Promise<void> {
    try {
      await store.patchGroup(patch);
      this.#setError("");
      this.#flashSaved();
    } catch (err) {
      this.#setError(err instanceof ApiError ? err.detail : t("couldNotSave"));
    }
  }

  #flashSaved(): void {
    const hint = this.querySelector<HTMLElement>(".js-saved-hint");
    if (!hint) return;
    hint.hidden = false;
    if (this.#savedTimer !== null) window.clearTimeout(this.#savedTimer);
    this.#savedTimer = window.setTimeout(() => {
      hint.hidden = true;
      this.#savedTimer = null;
    }, SAVED_FLASH_MS);
  }

  #setError(message: string): void {
    const banner = this.querySelector<HTMLElement>(".js-error");
    if (banner) {
      banner.hidden = !message;
      banner.textContent = message;
    }
  }

  #update(): void {
    const active = this.#activeElement();
    const group = store.group;

    const titleInput = this.querySelector<HTMLInputElement>(".js-title");
    if (titleInput && titleInput !== active) {
      titleInput.value = group?.projectTitle ?? "";
    }

    const changeInput = this.querySelector<HTMLTextAreaElement>(".js-change");
    if (changeInput && changeInput !== active) {
      changeInput.value = group?.changeSentence ?? "";
    }

    if (store.definitions && !this.querySelector(".js-picks")?.children.length) {
      this.#buildPicks();
    }

    if (store.definitions && !this.querySelector(".js-beats")?.children.length) {
      this.#buildHow();
    }

    for (const key of GROUP_PICK_KEYS) {
      const select = this.querySelector<HTMLSelectElement>(`.js-pick-${key}`);
      if (select && select !== active) {
        select.value = (group?.[key as GroupPickKey] as string | undefined) ?? "";
      }
    }

  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

if (!customElements.get("scapescene-group-screen")) {
  customElements.define("scapescene-group-screen", WorkshopGroupScreen);
}

export {};
